const authenticationPolicyEn = {
  aal: {
    aal1: {
      label: 'Single factor'
    },
    aal2: {
      label: 'Multi-factor (MFA)'
    }
  },
  action: {
    addCondition: 'Add condition',
    addConditionGroup: 'Add nested group',
    addRequirement: 'Add requirement',
    addRule: 'Add rule',
    removeCondition: 'Remove condition',
    removeRequirement: 'Remove requirement',
    removeRule: 'Remove rule'
  },
  condition: {
    aalLevelLabel: 'Minimum authentication strength',
    attribute: {
      DEVICE_IN_GROUPS: 'Device group',
      IDENTITY_MFA_ENROLLED: 'MFA enrolled',
      LOGIN_IDENTIFIER: 'Login identifier',
      NETWORK_IN: 'Network',
      SESSION_AAL_AT_LEAST: 'Authentication strength',
      SESSION_IS_FEDERATED: 'SSO session',
      USED_LOGIN_METHOD: 'Login method'
    },
    attributePlaceholder: 'Attribute',
    deviceGroupIdsLabel: 'Device groups',
    deviceGroupLoadError: 'Could not load device groups. Please reload.',
    deviceGroupLoading: 'Loading device groups…',
    deviceGroupPlaceholder: 'Select at least one device group',
    identifierLabel: 'Identifier type',
    methodRefsLabel: 'Login methods (matches any)',
    methodRefsLoadError: 'Could not load SSO connections. Login methods for this condition cannot be changed. Please reload.',
    methodRefsLoading: 'Loading SSO connections…',
    methodRefsPlaceholder: 'Select at least one login method',
    methodRefsUnknown: 'Unknown SSO connection',
    networkIdsLabel: 'Networks',
    networkIpUnset: 'IP not set',
    networkIpUnsetWarning: 'Some selected networks have no IP registered. They will not match until an IP is set in the network ledger.',
    networkLoadError: 'Could not load networks. Please reload.',
    networkLoading: 'Loading networks…',
    networkPlaceholder: 'Select at least one network',
    operator: {
      atLeast: 'at least',
      belongs: 'belongs to',
      below: 'below',
      includes: 'is included in',
      is: 'is',
      isNot: 'is not',
      notBelongs: 'does not belong to',
      notIncludes: 'is not included in',
      trueLiteral: 'true'
    },
    operatorPlaceholder: 'Operator'
  },
  conditionKind: {
    DEVICE_IN_GROUPS: {
      description: 'The device in use belongs to one of the selected groups.',
      label: 'Device belongs to a group'
    },
    IDENTITY_MFA_ENROLLED: {
      description: 'The user has enrolled in MFA (multi-factor authentication).',
      label: 'MFA enrolled'
    },
    LOGIN_IDENTIFIER: {
      description: 'Which kind of address was used to sign in — work or personal.',
      label: 'Login identifier type'
    },
    NETWORK_IN: {
      description: 'The source IP falls within one of the selected networks.',
      label: 'Source network'
    },
    SESSION_AAL_AT_LEAST: {
      description: 'The session meets at least the selected authentication strength.',
      label: 'Authentication strength is at least'
    },
    SESSION_IS_FEDERATED: {
      description: 'The session was established through SSO.',
      label: 'Session is an SSO session'
    },
    USED_LOGIN_METHOD: {
      description: 'The login method used matches any of the selected ones.',
      label: 'Login method used'
    }
  },
  decision: {
    ALLOW: {
      description: 'Allow sign-in. You can add further requirements below.',
      label: 'Allow'
    },
    DENY: {
      description: 'Block sign-in. No additional requirements can be set for a denial.',
      label: 'Deny'
    }
  },
  enforceFrom: {
    datePlaceholder: 'Select a date',
    description: 'Until the date and time you set, this policy is not evaluated. Evaluation falls through to the next applicable policy (or the default).',
    immediateDescription: 'Start evaluating this policy immediately after saving.',
    immediateTitle: 'Effective immediately',
    label: 'Set an effective date',
    scheduledDescription: 'Start evaluating this policy at a specified date and time.',
    scheduledTitle: 'Schedule date and time',
    sectionTitle: 'Effective date'
  },
  flow: {
    conditionsHoverTitle: 'Condition details',
    description: 'Evaluation starts at the top. Matching a rule ends at its THEN. Hover an IF node to inspect conditions.',
    groupJoin: 'Group ({{join}})',
    match: 'Match',
    requirementCount: '{{count}} extra requirements',
    start: 'Start',
    title: 'Evaluation flow',
    valueCount: '{{count}} selected'
  },
  join: {
    AND: 'Match all conditions (AND)',
    OR: 'Match any condition (OR)',
    label: 'How to combine conditions'
  },
  loginIdentifier: {
    personal: 'Personal email',
    work: 'Work email'
  },
  loginMethod: {
    password: 'Password'
  },
  membershipType: {
    INDIVIDUAL: 'Member',
    OPERATOR: 'Staff'
  },
  otherwise: {
    actionKind: {
      ALLOW: 'Allow',
      DENY: 'Deny',
      EVALUATE_NEXT: 'Evaluate next rule',
      label: 'OTHERWISE action'
    }
  },
  otherwiseTruncateConfirm: {
    description: 'Setting a concrete OTHERWISE makes this rule terminal and removes any rules after it.',
    title: 'Remove following rules'
  },
  outcome: {
    actionKind: {
      ALLOW: 'Allow',
      DENY: 'Deny',
      label: 'Action'
    },
    additionalSettings: 'Additional settings',
    allowedLoginMethods: {
      description: 'Restrict which login methods are accepted. At least one must be selected.',
      label: 'Allowed login methods',
      loadError: 'Could not load SSO connections, so only Password is listed. Reload before restricting login methods — saving now could lock out SSO users.',
      loading: 'Loading SSO connections\u2026',
      restrict: 'Restrict login methods',
      searchPlaceholder: 'Search login methods',
      selectPlaceholder: 'Select login methods',
      unrestricted: 'No restrictions'
    },
    authenticationRequirement: {
      description: 'Passing any one of these is enough (anyOf). Empty means no strength requirement.',
      empty: 'No additional requirements.',
      label: 'Additional authentication requirements',
      none: 'Not required',
      requireMfa: 'Require multi-factor authentication'
    },
    denyDiscardsObligations: 'Saving this rule as Deny discards its additional requirements, session freshness limit, allowed login methods, and personal email setting.',
    maxAuthAge: {
      enable: 'Limit session freshness',
      label: 'Session freshness limit',
      placeholder: 'Minutes (1–10080)',
      unrestricted: 'No limit'
    },
    personalEmailMode: {
      label: 'Personal email address'
    },
    title: 'Action'
  },
  personalEmailMode: {
    ALWAYS_ALLOWED: 'Always allowed',
    DISALLOWED: 'Not allowed',
    UNSET: 'Not specified in this rule',
    UNTIL_WORK_EMAIL_SET: 'Allowed until a work email is set'
  },
  requirement: {
    AAL_AT_LEAST: 'Authentication strength is at least',
    FEDERATED_SESSION: 'Must be an SSO session'
  },
  review: {
    blockedCount: '{{count}} items must be resolved before completing',
    blockedTitle: 'Resolve the following to complete',
    enforceFromImmediate: 'Effective immediately',
    enforceFromScheduled: 'Effective from {{date}}',
    unnamed: '(Untitled)',
    validateFailed: 'Could not validate the policy. Try again in a moment.',
    validateFailureNoDetail: 'The server did not return any details.',
    validateRejected: 'The server rejected the input. Check the following items.',
    validating: 'Validating…'
  },
  rule: {
    catchAllBadge: 'Fallback',
    catchAllDescription: 'Applied when none of the rules above match. Conditions cannot be added.',
    catchAllTitle: 'Fallback rule',
    conditionsTitle: 'Conditions',
    denyAllWarning: 'There are no allow rules. If applied as is, everyone this policy targets will be denied sign-in. Add an allow rule.',
    denyTerminalDescription: 'If none of the rules above match, access is denied. This terminal cannot be moved or deleted.',
    emptyConditions: 'No conditions yet. Add at least one.',
    groupTitle: 'Condition group',
    ifBadge: 'IF',
    ifMatched: 'If these conditions match',
    legacyDenyNote: 'This rule keeps the deny action from its existing configuration. Recreate the rule to change it to allow.',
    nameLabel: 'Display name',
    namePlaceholder: 'e.g. Network-based access control',
    otherwiseBadge: 'OTHERWISE',
    otherwiseDescription: 'If conditions do not match, continue to “{{next}}”.',
    otherwiseEvaluateNext: 'Evaluate next rule',
    otherwiseLabel: 'Otherwise',
    summary: {
      evaluateNext: 'evaluate with the next rule',
      matchPrefix: 'Matching access is',
      otherwisePrefix: '; otherwise',
      suffix: '.'
    },
    terminalBadge: '{{decision}} (terminal)',
    thenBadge: 'THEN',
    thenMatched: 'On match: {{decision}}',
    thenMatchedShort: 'On match',
    untitled: 'Untitled rule'
  },
  status: {
    DRAFT: {
      label: 'Draft'
    },
    ENFORCED: {
      label: 'Enforced'
    },
    REPORT_ONLY: {
      label: 'Report only'
    }
  },
  validation: {
    ALLOWED_LOGIN_METHODS_REQUIRED: 'Select at least one allowed login method. An empty list would deny every method.',
    APPLICATION_SCOPE_REQUIRED: 'Select at least one application, or turn off the scope restriction.',
    ASSIGNMENT_REQUIRED: 'Select at least one target.',
    CONDITION_REQUIRES_VALUE: 'Select at least one value for this condition.',
    DEFAULT_POLICY_FINAL_RULE_MUST_ALLOW: 'The fallback rule of the default policy cannot deny access, because it would lock everyone out.',
    DEFAULT_POLICY_MUST_NOT_HAVE_OTHERWISE: 'The default policy cannot set OTHERWISE. Unmatched conditions always continue to the next rule.',
    ENFORCE_FROM_INCOMPLETE: 'Enter both a date and a time for the effective date.',
    GROUP_REQUIRES_CONDITION: 'A condition group needs at least one condition.',
    MAX_AUTH_AGE_OUT_OF_RANGE: 'Session freshness must be a whole number between 1 and 10080 minutes.',
    NAME_REQUIRED: 'Enter a name.',
    OTHERWISE_ALLOWED_LOGIN_METHODS_REQUIRED: 'Select at least one login method for OTHERWISE. An empty list denies every method.',
    OTHERWISE_ON_CATCH_ALL_FORBIDDEN: 'A condition-less rule cannot have OTHERWISE.',
    POLICY_MUST_BE_TOTAL: 'The final rule must be total. Clear its conditions or set a concrete OTHERWISE action.',
    POLICY_REQUIRED: 'Select an authentication policy.',
    UNREACHABLE_RULE: 'A non-final rule is total, so later rules are never evaluated. Set OTHERWISE to evaluate next, or remove later rules.',
    conditionsStepSummary: 'Some access conditions are incomplete'
  }
};
export default authenticationPolicyEn;